import { PRODUCT_NAME } from "@digital-signage/brand";
import { BrandMark } from "@digital-signage/brand/react";
import type {
  PlatformComponentStatus,
  PlatformServiceComponent,
  PlatformStatusEventView,
} from "@digital-signage/contracts";
import {
  Activity,
  CalendarClock,
  CheckCircle2,
  CircleAlert,
  Clock3,
  CloudOff,
  Database,
  MonitorCheck,
  RefreshCw,
  ServerCog,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { loadStatusFeed, type LoadedStatusFeed } from "./status-feed.js";

const componentLabels: Readonly<Record<PlatformServiceComponent, string>> = {
  api: "API",
  content_delivery: "Content delivery",
  portal: "Tenant portal",
};

const statusLabels: Readonly<Record<PlatformComponentStatus, string>> = {
  degraded: "Degraded performance",
  maintenance: "Maintenance",
  major_outage: "Major outage",
  operational: "Operational",
};

const overallLabels: Readonly<Record<PlatformComponentStatus, string>> = {
  degraded: "Some systems are degraded",
  maintenance: "Maintenance is in progress",
  major_outage: "Service disruption",
  operational: "All systems operational",
};

const PRODUCTION_STALE_AFTER_MS = 3 * 60_000;
const STAGING_STALE_AFTER_MS = 35 * 60_000;
const STAGING_STATUS_HOSTNAME = "digital-signage-status-staging.axiossoft.com";

export function statusFeedStaleAfterMs(hostname: string): number {
  return hostname === STAGING_STATUS_HOSTNAME
    ? STAGING_STALE_AFTER_MS
    : PRODUCTION_STALE_AFTER_MS;
}

function dateTime(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function componentIcon(component: PlatformServiceComponent) {
  if (component === "portal") return MonitorCheck;
  if (component === "api") return ServerCog;
  return Database;
}

function eventIsOpen(event: PlatformStatusEventView): boolean {
  return !["resolved", "cancelled"].includes(event.current.status);
}

function eventStatusLabel(event: PlatformStatusEventView): string {
  return event.current.status.replaceAll("_", " ");
}

function OverallIcon({ status }: { status: PlatformComponentStatus }) {
  if (status === "operational") return <CheckCircle2 aria-hidden="true" size={27} />;
  if (status === "maintenance") return <CalendarClock aria-hidden="true" size={27} />;
  return <CircleAlert aria-hidden="true" size={27} />;
}

function StatusEvent({ event }: { event: PlatformStatusEventView }) {
  const open = eventIsOpen(event);
  return (
    <article className={`status-event status-event--${event.kind}`}>
      <header className="status-event__header">
        <div className="status-event__identity">
          {event.kind === "incident" ? (
            <CircleAlert aria-hidden="true" size={20} />
          ) : (
            <CalendarClock aria-hidden="true" size={20} />
          )}
          <div>
            <span>{event.kind}</span>
            <h3>{event.title}</h3>
          </div>
        </div>
        <span className={`event-state event-state--${open ? "open" : "closed"}`}>
          {eventStatusLabel(event)}
        </span>
      </header>
      <div className="status-event__summary">
        <p>{event.current.summary}</p>
        <span>
          {dateTime(event.startedAt)} · {event.affectedComponents.map((component) => componentLabels[component]).join(", ")}
        </span>
      </div>
      {event.updates.length > 1 ? (
        <details className="status-event__updates">
          <summary>{event.updates.length} updates</summary>
          <ol>
            {[...event.updates].reverse().map((update) => (
              <li key={update.sequence}>
                <div>
                  <strong>{update.status.replaceAll("_", " ")}</strong>
                  <time dateTime={update.occurredAt}>{dateTime(update.occurredAt)}</time>
                </div>
                <p>{update.summary}</p>
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </article>
  );
}

function StatusContent({ feed }: { feed: LoadedStatusFeed }) {
  const { status } = feed;
  const stale = Date.now() - Date.parse(status.generatedAt) >
    statusFeedStaleAfterMs(window.location.hostname);
  const openEvents = status.serviceEvents.filter(eventIsOpen);
  const history = status.serviceEvents.filter((event) => !eventIsOpen(event));

  return (
    <>
      {stale || feed.source !== "live" ? (
        <div className="feed-warning" role="status">
          <CloudOff aria-hidden="true" size={18} />
          <div>
            <strong>Live updates are delayed</strong>
            <span>Showing the last verified status from {dateTime(status.generatedAt)}.</span>
          </div>
        </div>
      ) : null}

      <section className={`overall-status overall-status--${status.overallStatus}`}>
        <OverallIcon status={status.overallStatus} />
        <div>
          <span>Current status</span>
          <h1>{overallLabels[status.overallStatus]}</h1>
        </div>
        <time dateTime={status.generatedAt}>Updated {dateTime(status.generatedAt)}</time>
      </section>

      <section aria-label="Component status" className="component-grid">
        {status.components.map(({ component, status: componentStatus }) => {
          const Icon = componentIcon(component);
          return (
            <article className="component-status" key={component}>
              <div>
                <Icon aria-hidden="true" size={20} />
                <strong>{componentLabels[component]}</strong>
              </div>
              <span className={`component-state component-state--${componentStatus}`}>
                <i aria-hidden="true" />
                {statusLabels[componentStatus]}
              </span>
            </article>
          );
        })}
      </section>

      <section className="status-section">
        <header className="section-heading">
          <div>
            <span>Current</span>
            <h2>Active events</h2>
          </div>
          <span className="section-count">{openEvents.length}</span>
        </header>
        {openEvents.length ? (
          <div className="status-event-list">
            {openEvents.map((event) => <StatusEvent event={event} key={event.id} />)}
          </div>
        ) : (
          <div className="empty-state">
            <CheckCircle2 aria-hidden="true" size={22} />
            <span>No active incidents or maintenance.</span>
          </div>
        )}
      </section>

      <section className="status-section">
        <header className="section-heading">
          <div>
            <span>Past 12 months</span>
            <h2>Incident history</h2>
          </div>
          <Activity aria-hidden="true" size={19} />
        </header>
        {history.length ? (
          <div className="status-event-list">
            {history.map((event) => <StatusEvent event={event} key={event.id} />)}
          </div>
        ) : (
          <div className="empty-state">
            <Clock3 aria-hidden="true" size={22} />
            <span>No incidents in the reporting window.</span>
          </div>
        )}
      </section>
    </>
  );
}

export function StatusApp() {
  const [feed, setFeed] = useState<LoadedStatusFeed | null>(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      setFeed(await loadStatusFeed());
      setError(false);
    } catch {
      setError(true);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  const sourceLabel = useMemo(() => {
    if (!feed) return "Connecting";
    return feed.source === "live" ? "Live feed" : "Last known status";
  }, [feed]);

  return (
    <div className="status-shell">
      <header className="status-header">
        <div className="brand-lockup">
          <BrandMark className="brand-mark" />
          <div><strong>{PRODUCT_NAME}</strong><span>Service status</span></div>
        </div>
        <div className="status-header__actions">
          <span className="feed-source"><i aria-hidden="true" />{sourceLabel}</span>
          <button
            aria-label="Refresh status"
            className="icon-button"
            disabled={refreshing}
            onClick={() => void refresh()}
            title="Refresh status"
            type="button"
          >
            <RefreshCw aria-hidden="true" className={refreshing ? "spin" : undefined} size={18} />
          </button>
        </div>
      </header>

      <main className="status-main">
        {feed ? <StatusContent feed={feed} /> : error ? (
          <section className="fatal-status" role="alert">
            <CloudOff aria-hidden="true" size={28} />
            <h1>Status is temporarily unavailable</h1>
            <button className="button" onClick={() => void refresh()} type="button">Try again</button>
          </section>
        ) : (
          <div className="loading-status" role="status">
            <span aria-hidden="true" />
            Loading service status
          </div>
        )}
      </main>

      <footer className="status-footer">
        <span>Platform status is measured from independent external checks.</span>
        <span>Screen playback continues from local cache during platform outages.</span>
      </footer>
    </div>
  );
}
